import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '@/api/auth';
import { Mail, ArrowLeft } from 'lucide-react';

export function ForgotRoute() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-bmail-navy px-4">
      <div className="w-full max-w-sm rounded-2xl bg-bmail-surface p-6 shadow-xl border border-bmail-border">
        <div className="mb-8 flex items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-bmail-goldfill text-bmail-on-accent">
            <Mail size={28} strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl font-bold text-bmail-text tracking-tight">
            <span className="text-bmail-gold">B-</span>Mail
          </h1>
        </div>

        {sent ? (
          <div className="text-center space-y-4">
            <p className="text-bmail-text">
              If an account exists for <span className="text-bmail-gold font-medium">{email}</span>,
              a reset link is on its way. Check your inbox.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm text-bmail-text-secondary hover:text-bmail-gold transition-colors"
            >
              <ArrowLeft size={16} /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h2 className="mb-2 text-center text-xl font-semibold text-bmail-text">Reset your password</h2>
            <p className="mb-6 text-center text-sm text-bmail-text-secondary">
              Enter your email and we'll send you a reset link.
            </p>

            {error && (
              <div className="mb-4 rounded-lg bg-bmail-error/10 px-4 py-3 text-sm text-bmail-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-bmail-text">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-lg bg-bmail-goldfill px-4 py-3 font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-bmail-text-secondary hover:text-bmail-gold transition-colors"
              >
                <ArrowLeft size={16} /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}